import { useEffect, useState } from 'react';

import { getHealth } from '../api';
import { SKEW_WARN_THRESHOLD_MS } from '../config';

export interface ClockSkew {
  /** device_now - server_now, in ms. null until the probe completes. */
  skewMs: number | null;
  /** True when |skew| exceeds the soft threshold and payroll accuracy is at risk. */
  warn: boolean;
}

/**
 * Soft clock-skew check. `punchedAt` is client-supplied and drives payroll, so a
 * wrong device clock silently corrupts hours (plan-v6 §Clock skew/trust). On load
 * we compare the device clock to the server's `/health` timestamp and warn the
 * operator if they diverge — non-blocking; the server also flags large skews for
 * supervisor review.
 */
export function useClockSkew(): ClockSkew {
  const [skewMs, setSkewMs] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    void getHealth()
      .then((health) => {
        const serverMs = Date.parse(health.timestamp);
        if (cancelled || Number.isNaN(serverMs)) {
          return;
        }
        setSkewMs(Date.now() - serverMs);
      })
      .catch(() => {
        // Offline or health unreachable — no skew signal, leave null (no warning).
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return {
    skewMs,
    warn: skewMs !== null && Math.abs(skewMs) > SKEW_WARN_THRESHOLD_MS,
  };
}
